/**
 * `DiceStage` — the imperative three.js render/playback engine behind the overlay. It owns the
 * WebGL renderer, a top-down orthographic camera, and the live dice.
 *
 * **Lifetime: one stage per session, built at boot.** {@link ./DiceOverlay} constructs exactly
 * one of these during app startup, calls {@link warmup} to compile the shader programs, and then
 * holds it — it is never disposed between rolls. What idles is the **CPU**, not the GPU: the rAF
 * loop does not run at all until {@link spawnBatch} starts it, and {@link tick} stops scheduling
 * itself the moment the table is empty. A create-on-first-throw / `dispose()`-on-idle lifecycle
 * would instead pay a **WebGL context creation and a full shader compile on every single roll**,
 * both synchronous main-thread work landing in the click path of someone's turn.
 *
 * Two throw paths, both **baked-correction replay**: {@link throwDie} fires a physics-decides dev
 * d20 (lands where it lands); {@link throwSpecs} takes one die per {@link DiceSpec}, each
 * choreographed to land on its recorded value. Neither path runs live physics:
 * {@link spawnBatch} pre-rolls the whole batch headlessly
 * ({@link recordThrowBatch}), which records each die's full collision-inclusive trajectory to
 * rest and computes the one fixed rotation `C` per die that carries its natural rest onto the
 * target-showing orientation. The visible pass then just plays the recorded buffer back with
 * `C` baked into every frame from frame zero — the die lands exactly on the recorded face with
 * **no post-settle snap**, because a constant rotation applied to a continuous trajectory stays
 * continuous. Dropped dice (keep-highest / crit losers) are thrown too, dimmed, so the player
 * sees the full pool without them being a second source of truth for the total.
 *
 * Render ↔ record: the mesh geometry and the recording's physics body are built from the same
 * {@link DieGeometry} (resolved by `geometryForSides` inside {@link recordThrowBatch}), so the
 * drawn solid matches the simulated one exactly. When every die's buffer is exhausted (and
 * lingered briefly), the loop stops and `onIdle` fires so the overlay can hide the canvas.
 *
 * Camera/world mapping: the table is the `XZ` plane (`+Y` up); the camera looks straight down
 * `-Y`. The visible half-extents track the viewport aspect ratio (keyed to the **shorter**
 * dimension so a die never balloons on a narrow/portrait window) — each throw's pre-roll pass
 * builds its own throwaway physics table at the current bounds (see {@link recordThrowBatch}),
 * so the containment walls always hug the live viewport edge.
 *
 * **One throw, one batch, one clock.** Each throw is its own batch with its own
 * settle→fade→reap timeline, and a new throw retires any already-settled batch outright — the
 * bookkeeping is the pure {@link ./dicePlayback} state machine, this class is the mesh adapter
 * over it. Appending every throw into one ever-growing die list under one shared settle clock
 * would leave a barrage's shot 1 frozen at rest until shot 2 also landed.
 *
 * Disappear: once a batch is `done`, its dice hold for `LINGER_MS` while {@link fadeAt}
 * ({@link ./diceFade}) ramps that batch's fade 0→1 — both the solid and its edge lines animate
 * opacity `1 - fade` — then the batch is reaped. `onIdle` fires once nothing is left on the table.
 */
import * as THREE from "three";
import { recordThrowBatch, shownQuatAt, type RecordedDie } from "./bakedCorrection";
import { D20_GEOMETRY, type DieGeometry } from "./dieGeometry";
import type { ThrowBounds } from "./diceBody";
import type { DiceSpec } from "./rollChoreography";
import { buildFlatUVs } from "./faceNumerals";
import { numeralAtlasFor } from "./numeralTexture";
import { addBatch, advance, batchFade, createPlayback, isIdle, reap, type PlaybackDie } from "./dicePlayback";

/** Die circumradius (world units). Drives the render mesh, the physics body, and the wall inset. */
export const RADIUS = 1.5;
/**
 * Half-extent (world units) mapped to half the **shorter** viewport dimension. A die of
 * diameter `2*RADIUS` is therefore `2*RADIUS / (2*VIEW_HALF_MIN)` of the shorter screen
 * dimension on any aspect ratio — keep this large enough that the die stays a sensible
 * fraction of the viewport (the `diceSizing` guard pins the ceiling).
 */
export const VIEW_HALF_MIN = 16;
/** The cadence the pre-roll recorded at: one recorded frame per 1/60s of *simulated* time. */
const RECORD_FPS = 60;
/**
 * How many recorded frames to play per 1/60s of real time — the tumble's **speed dial**.
 *
 * A recorded trajectory played at rate `k` is not a "fast-forward artifact": it is exactly the
 * trajectory the same throw would have traced in a world with `k²`× the gravity and `k`× the
 * launch speed. Every frame is still a real collided rigid-body pose, so the die genuinely comes
 * to rest — there is nothing to snap, clamp or truncate here. That's why the speed-up is split
 * between this dial and the physics itself (gravity + damping, see `./diceWorld` / `./diceBody`)
 * rather than pushed entirely into either: cannon-es gets unstable and stops settling reliably
 * long before physics alone reaches the target duration, while doing it all here would leave the
 * pre-roll simulating frames nobody ever sees.
 *
 * Sanity check on "does 2× look wrong": the arena is ~9 die-diameters across and a die crosses it
 * in ~0.8s at this rate. A real d20 thrown across a real table crosses that distance far faster
 * than the ~3s a rate of 1 would imply.
 */
const PLAYBACK_RATE = 2;
/**
 * Hold a settled batch on screen before reaping it — also the window its fade animates across.
 * Reading the result doesn't depend on this: the readout panel outlives the dice and keeps a
 * short stack of recent rolls ({@link ./DiceOverlay}), so this governs only how long the *dice*
 * sit there after landing.
 */
const LINGER_MS = 300;

type StageCallbacks = {
  /** Called with the final shown value when a die finishes (physics value, or the choreographed target). */
  onRead?: (value: number) => void;
  /**
   * Called once every die has finished and the linger elapsed, as the rAF loop stops. "The table
   * is empty — hide me," **not** "dispose me": the stage is permanent (see the file header).
   */
  onIdle?: () => void;
  /**
   * Called the moment **one batch's** last die lands, with the id {@link throwSpecs} returned for
   * it — "shot 1's dice have stopped rolling," which is a different and much earlier event than
   * `onIdle` ("the table is empty"). A barrage reaches `onIdle` only after BOTH shots have landed
   * and lingered out, so anything gating a reveal on a specific throw has to listen here.
   *
   * Fires at **settle**, not at reap: the dice have stopped moving and the number is legible; the
   * 300ms linger/fade that follows is the dice leaving, not the roll finishing.
   */
  onBatchSettled?: (batchId: number) => void;
};

/** What the pure {@link ./dicePlayback} state machine carries for us: the recording and its mesh. */
type DiePayload = {
  recorded: RecordedDie;
  mesh: THREE.Object3D;
  /** Drive the die's disappear fade (0 = fully opaque, 1 = fully faded out) — see {@link buildMesh}. */
  setFade: (value: number) => void;
};

export class DiceStage {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.OrthographicCamera;
  private bounds: ThrowBounds;
  private readonly playback = createPlayback<DiePayload>();
  private raf = 0;
  private running = false;
  private lastTime = 0;
  private frameAccum = 0; // fractional recorded-frames carried between ticks
  private disposed = false;
  /**
   * The die drawn by {@link warmup}, kept alive (invisible, still parented to the scene) for the
   * stage's whole life — see that method for why disposing it would undo the warmup.
   */
  private warmupMesh: THREE.Object3D | null = null;
  private readonly callbacks: StageCallbacks;
  private readonly handleResize = () => this.resize();

  constructor(canvas: HTMLCanvasElement, callbacks: StageCallbacks = {}) {
    this.callbacks = callbacks;
    // `alpha` is required (the overlay is transparent over the live app) and `antialias` is a
    // deliberate keep, NOT an unexamined default: a cheaper context configuration was investigated
    // as a possible fix for the e2e tier's context failures and measured irrelevant — under the
    // default `harness.launchBrowser` flags EVERY configuration is refused (`alpha:false`,
    // `antialias:false`, `powerPreference:"low-power"`, bare `{}`, webgl1 and webgl2 alike), and
    // under `--use-gl=swiftshader` every one of them succeeds. The knob does not move that needle,
    // so paying for it buys smooth die edges at no measured reliability cost.
    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.scene = new THREE.Scene();
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(6, 18, 8);
    this.scene.add(key);

    this.bounds = { halfWidth: VIEW_HALF_MIN, halfDepth: VIEW_HALF_MIN };
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
    this.camera.position.set(0, 40, 0);
    this.camera.up.set(0, 0, -1); // screen-up = world -Z
    this.camera.lookAt(0, 0, 0);

    this.resize();
    // The stage outlives every roll now, so it tracks the viewport for its whole life rather than
    // only while dice are in flight. The handler is pure bookkeeping plus a drawing-buffer realloc
    // — no render, no physics — so an idle stage still costs no frame time.
    window.addEventListener("resize", this.handleResize);
    // No rAF here on purpose: a stage built at boot has nothing to play, and starting the loop
    // would immediately hit `isIdle` and fire `onIdle` at an overlay that has never seen a throw.
    // {@link spawnBatch} starts the loop; {@link tick} stops it.
  }

  /**
   * Draw one real die for one frame, to force three.js to compile its shader programs **now**
   * rather than in the click path of the session's first roll. Called once, by the overlay,
   * immediately after construction.
   *
   * Three things here are load-bearing and each of them is a way to write a warmup that compiles
   * nothing:
   *
   *  - **It goes through the real {@link buildMesh}.** The programs three.js caches are keyed on
   *    the material's own configuration — `MeshStandardMaterial` with `flatShading`, a `map` and
   *    `transparent`, plus the scene's ambient+directional lights, and separately the edge lines'
   *    `LineBasicMaterial`. A stand-in cube with a plain material compiles a *different* program
   *    and warms nothing. One `buildMesh` covers both of the programs a throw needs; the per-shape
   *    numeral atlas differs between dice but the program does not, so a d20 warms them all.
   *  - **The die is drawn IN VIEW, not parked off-camera.** three.js frustum-culls before it sets
   *    up an object's program, so the cheap-looking "render it where nobody can see it" version
   *    skips the compile entirely. The canvas is CSS-hidden while idle ({@link ./DiceOverlay}), so
   *    an in-view warmup frame is invisible anyway — that is what makes this safe.
   *  - **The mesh is never disposed.** `Material.dispose()` releases the material's reference to
   *    its compiled program, and three.js deletes the program once the last reference goes — so
   *    tidying the warmup die away would throw away the exact thing the warmup just paid for.
   *    It stays parented to the scene with `visible = false` (skipped per frame for the cost of
   *    one traversal step) so the program cache keeps a live reference for the session.
   */
  warmup(): void {
    if (this.disposed || this.warmupMesh !== null) return;
    const { mesh } = this.buildMesh(D20_GEOMETRY, true);
    this.scene.add(mesh); // at the origin — dead centre of the camera's frustum, so it really draws
    this.renderer.render(this.scene, this.camera);
    mesh.visible = false;
    this.warmupMesh = mesh;
  }

  /**
   * three.js's live shader-program cache size. **Test/diagnostic read only** — the overlay
   * republishes it on `window.__diceStageDebug` so a real browser can answer "were the shaders
   * compiled at boot?" with a console read taken *before* the session's first roll. A non-zero
   * count with zero rolls behind it means the warmup did its job.
   */
  programCount(): number {
    return this.renderer.info.programs?.length ?? 0;
  }

  /**
   * Start the playback loop if it isn't already running. The clock is re-based on every start:
   * `lastTime` would otherwise be however long ago the last roll ended, and the leftover
   * `frameAccum` would spend a fraction of a frame the new batch never simulated.
   */
  private start(): void {
    if (this.running || this.disposed) return;
    this.running = true;
    this.lastTime = performance.now();
    this.frameAccum = 0;
    this.raf = requestAnimationFrame(this.tick);
  }

  /**
   * Recompute the visible bounds + camera frustum from the current viewport size. Keyed to the
   * **shorter** viewport dimension: the shorter world half-extent is always {@link VIEW_HALF_MIN},
   * the longer one scales with the aspect ratio (`halfWidth = halfDepth * aspect`, so dice render
   * un-stretched). That keeps the die a fixed fraction of the shorter screen dimension on any
   * window shape — it never balloons on a tall/portrait window the way a height-keyed frustum
   * would. The next throw's pre-roll pass reads the updated `bounds` and builds its own
   * containment table at the new size (see {@link recordThrowBatch}) — there's no persistent
   * physics world here to reposition.
   */
  private resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const aspect = w / h;
    let halfWidth: number;
    let halfDepth: number;
    if (aspect >= 1) {
      // Landscape: viewport height (world Z/depth) is the shorter dimension.
      halfDepth = VIEW_HALF_MIN;
      halfWidth = halfDepth * aspect;
    } else {
      // Portrait: viewport width (world X) is the shorter dimension.
      halfWidth = VIEW_HALF_MIN;
      halfDepth = halfWidth / aspect;
    }
    this.bounds = { halfWidth, halfDepth };

    this.camera.left = -halfWidth;
    this.camera.right = halfWidth;
    this.camera.top = halfDepth;
    this.camera.bottom = -halfDepth;
    this.camera.updateProjectionMatrix();

    // `updateStyle=true` (the default) is load-bearing on any HiDPI display: it makes three.js
    // set `canvas.style.width/height` to the CSS-pixel viewport size, separate from the
    // drawing-buffer resolution (`canvas.width/height`, scaled by `setPixelRatio`). Without it,
    // a `<canvas>` with no CSS width/height (this one relies on `.lvtt-dice-canvas`'s
    // `position:fixed; inset:0`) falls back to its *intrinsic* size — the already
    // dPR-multiplied drawing-buffer dimensions — so the canvas's actual CSS layout box becomes
    // `devicePixelRatio`× the real viewport, anchored top-left. On a dPR=2 display (any standard
    // Retina Mac) that puts 3/4 of the arena's rendered pixels outside the real window entirely,
    // which reads as "dice end up off screen" — a canvas CSS-sizing bug, not a physics/wall
    // containment one.
    this.renderer.setSize(w, h, true);
  }

  /**
   * Build a die's render mesh (faceted solid + edge lines) for its shape. Dropped dice render
   * dimmed. Each face carries a numeral via a per-shape atlas texture ({@link numeralAtlasFor})
   * mapped with UVs from the *same* geometry the atlas's cell layout was built against
   * ({@link buildFlatUVs}, mirroring this method's own position-triangulation loop 1:1) — a pure
   * visual layer over the existing `faceValues`/`faceNormals` tables, no second value mapping.
   * The solid and its edge lines fade out together on disappear: the returned handle multiplies
   * each material's base opacity by `1 - value`.
   */
  private buildMesh(die: DieGeometry, kept: boolean): { mesh: THREE.Object3D; setFade: (value: number) => void } {
    // Non-indexed positions (one vertex triple per triangle) → flat per-face shading. Faces
    // may be quads (the d6/d3 cube), so fan-triangulate each ring: [v0,vk,vk+1].
    const positions: number[] = [];
    for (const face of die.faces) {
      for (let k = 1; k < face.length - 1; k++) {
        for (const idx of [face[0]!, face[k]!, face[k + 1]!]) {
          const v = die.vertices[idx]!;
          positions.push(v[0] * RADIUS, v[1] * RADIUS, v[2] * RADIUS);
        }
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    const { texture, layout } = numeralAtlasFor(die);
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(buildFlatUVs(die, layout), 2));
    geometry.computeVertexNormals();

    const group = new THREE.Group();
    const solidMaterial = new THREE.MeshStandardMaterial({
      color: kept ? 0x6ad7ff : 0x415059,
      map: texture,
      metalness: 0.2,
      roughness: 0.45,
      flatShading: true,
      // Always transparent (not just `!kept`) so the disappear fade below has something to blend
      // against — the pre-fade base opacity is still `kept ? 1 : 0.4`.
      transparent: true,
      opacity: kept ? 1 : 0.4,
    });
    const baseSolidOpacity = solidMaterial.opacity;
    const solid = new THREE.Mesh(geometry, solidMaterial);
    group.add(solid);
    const edgesMaterial = new THREE.LineBasicMaterial({
      color: kept ? 0x0a2a3a : 0x1a2228,
      // Always transparent (not just `!kept`) so the disappear fade below has something to blend
      // against — the pre-fade base opacity is still `kept ? 1 : 0.4`.
      transparent: true,
      opacity: kept ? 1 : 0.4,
    });
    const baseEdgeOpacity = edgesMaterial.opacity;
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geometry), edgesMaterial);
    group.add(edges);
    return {
      mesh: group,
      setFade: (value: number) => {
        solidMaterial.opacity = baseSolidOpacity * (1 - value);
        edgesMaterial.opacity = baseEdgeOpacity * (1 - value);
      },
    };
  }

  /**
   * Pre-roll a batch of dice (see {@link recordThrowBatch}) and build their meshes, ready to
   * play back. `kept[i]` dims the die (keep-highest/crit losers still throw, just faded).
   *
   * The batch lands as its own {@link ./dicePlayback} batch, which retires any already-settled
   * batch still on the table — those dice are removed and their GPU resources released here.
   *
   * Returns the new batch's id, or `null` when nothing was spawned (an empty spec list, or a
   * disposed stage). A caller waiting on this throw must treat `null` as "no dice are coming" and
   * release its wait immediately rather than wait for an `onBatchSettled` that can never fire.
   */
  private spawnBatch(specs: readonly { sides: number; target: number | null; kept: boolean }[]): number | null {
    if (this.disposed || specs.length === 0) return null;
    const recorded = recordThrowBatch(
      specs.map((s) => ({ sides: s.sides, target: s.target })),
      { bounds: this.bounds, radius: RADIUS },
    );
    const entries = recorded.map((rec, i) => {
      const { mesh, setFade } = this.buildMesh(rec.geometry, specs[i]!.kept);
      this.scene.add(mesh);
      return { payload: { recorded: rec, mesh, setFade }, frameCount: rec.buffer.length };
    });
    const { id, retired } = addBatch(this.playback, entries);
    for (const payload of retired) this.releaseDie(payload);
    // The stage is permanent but its loop is not: this is the only thing that starts it, and a
    // throw landing on an idled-out stage restarts it here rather than rebuilding the stage.
    this.start();
    return id;
  }

  /** Spawn one physics-decides d20 (dev path). Returns its batch id — see {@link spawnBatch}. */
  throwDie(): number | null {
    return this.spawnBatch([{ sides: 20, target: null, kept: true }]);
  }

  /**
   * Choreograph a recorded roll: one die per spec, each (real shape) landing on its recorded face.
   * Returns this throw's batch id, which {@link StageCallbacks.onBatchSettled} reports back when
   * its dice stop rolling — see {@link spawnBatch} for the `null` case.
   */
  throwSpecs(specs: readonly DiceSpec[]): number | null {
    return this.spawnBatch(specs.map((s) => ({ sides: s.sides, target: s.value, kept: s.kept })));
  }

  /** Write a die's current playback frame (with its baked correction applied) onto its mesh. */
  private syncMesh(die: PlaybackDie<DiePayload>): void {
    const { recorded, mesh } = die.payload;
    const frame = recorded.buffer[die.frameIndex]!;
    const q = shownQuatAt(recorded, die.frameIndex);
    mesh.position.set(frame.pos[0], frame.pos[1], frame.pos[2]);
    mesh.quaternion.set(q.x, q.y, q.z, q.w);
  }

  /** Take a die off the table and free its GPU resources (its atlas texture is shared — left alone). */
  private releaseDie(payload: DiePayload): void {
    this.releaseMesh(payload.mesh);
  }

  /**
   * Detach a die mesh and dispose its geometry + materials. Disposing a material also drops its
   * reference to the compiled shader program, which is why {@link warmup}'s die is deliberately
   * never routed through here.
   */
  private releaseMesh(mesh: THREE.Object3D): void {
    this.scene.remove(mesh);
    mesh.traverse((obj) => {
      if (obj instanceof THREE.Mesh || obj instanceof THREE.LineSegments) {
        obj.geometry.dispose();
        const mat = obj.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat.dispose();
      }
    });
  }

  private readonly tick = (now: number): void => {
    if (this.disposed) return;
    const delta = Math.min((now - this.lastTime) / 1000, 1 / 30);
    this.lastTime = now;

    // No live physics during playback — just advance each die through its pre-recorded
    // buffer. `PLAYBACK_RATE` recorded frames per 1/60s of real time; the fractional
    // remainder carries to the next tick so the speed is exact rather than rounded per frame.
    // `delta` is clamped above, so a tab-stall can never queue an unbounded catch-up burst.
    this.frameAccum += delta * RECORD_FPS * PLAYBACK_RATE;
    const steps = Math.floor(this.frameAccum);
    if (steps > 0) {
      this.frameAccum -= steps;
      const { finished, settled } = advance(this.playback, steps, now);
      for (const die of finished) this.callbacks.onRead?.(die.payload.recorded.value);
      // Announced here, at settle, and before the fade below: a caller holding an outcome back is
      // waiting on "the dice stopped," not on them clearing the table.
      for (const batchId of settled) this.callbacks.onBatchSettled?.(batchId);
    }

    // Every batch runs its own settle → fade clock, so a barrage's first shot disappears on
    // schedule whether or not the second shot has landed yet.
    for (const batch of this.playback.batches) {
      const fade = batchFade(batch, now, LINGER_MS);
      for (const die of batch.dice) {
        this.syncMesh(die);
        die.payload.setFade(fade);
      }
    }
    for (const payload of reap(this.playback, now, LINGER_MS)) this.releaseDie(payload);

    this.renderer.render(this.scene, this.camera);

    if (isIdle(this.playback)) {
      // Stop scheduling — this is the CPU half of "idle costs nothing", and it is the WHOLE
      // teardown. The renderer, its context and its compiled programs all stay; `onIdle` only
      // tells the overlay to hide the canvas, and the next throw restarts this loop via `start()`.
      this.running = false;
      this.callbacks.onIdle?.();
      return;
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  /**
   * Stop the loop and release all GPU resources — **unmount only**, not a per-roll teardown. The
   * overlay calls this when the app tears the dice surface down (a React unmount, a test's
   * cleanup); a finished roll just idles the loop, see {@link tick}.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.handleResize);
    for (const batch of this.playback.batches) {
      for (const die of batch.dice) this.releaseDie(die.payload);
      // A batch dying mid-air still has to release whoever is waiting on it — an abandoned wait
      // that is never announced is an outcome stuck off the screen. Announcing it as settled is
      // honest enough for every caller: nothing more is coming from these dice.
      if (batch.settledAt === null) this.callbacks.onBatchSettled?.(batch.id);
    }
    this.playback.batches = [];
    if (this.warmupMesh !== null) {
      this.releaseMesh(this.warmupMesh);
      this.warmupMesh = null;
    }
    this.renderer.dispose();
  }
}
